// Pure helpers behind the visual template editor: matching Formstack's
// field list to the rectangles inside the PDF, colouring them by state,
// and converting signature boxes between screen pixels and PDF points.

import { TEMPLATE_BOX_ROLES } from "../templateSignatureBoxes";

export const FIELD_STATE = {
  mapped: { label: "Mapped", color: "#16a34a", fill: "rgba(22, 163, 74, 0.12)" },
  unmapped: { label: "Not mapped", color: "#d97706", fill: "rgba(217, 119, 6, 0.12)" },
  signature: { label: "Signature spot", color: "#7c3aed", fill: "rgba(124, 58, 237, 0.14)" },
  checkbox: { label: "Checkbox", color: "#64748b", fill: "rgba(100, 116, 139, 0.10)" },
};

export const BOX_TYPES = [
  { value: "signature", label: "Signature", width: 170, height: 32 },
  { value: "date", label: "Date", width: 110, height: 24 },
  { value: "initial", label: "Initials", width: 60, height: 24 },
  { value: "fullname", label: "Printed name", width: 170, height: 24 },
  { value: "text", label: "Text", width: 170, height: 24 },
];

export const BOX_TYPE_LABELS = Object.fromEntries(BOX_TYPES.map((t) => [t.value, t.label]));
export const ESIGN_BOX_TYPES = new Set(BOX_TYPES.map((t) => t.value));

export const SIGNER_ROLES = TEMPLATE_BOX_ROLES.map(({ role, name }) => ({ value: role, label: name }));
export const SIGNER_ROLE_LABELS = Object.fromEntries(SIGNER_ROLES.map((r) => [r.value, r.label]));

const normalizeName = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Attach page rectangles from the PDF layout to Formstack's field list.
 * Formstack's names usually equal the widget names; XFA forms differ
 * (`form1[0].Page1[0].P1_Name1_FLD[0]` is listed as `P1_Name1_FLD`), so we
 * try the short name, the exact name, then a punctuation-free comparison.
 * Radio groups share one name, so a field can have several rectangles.
 */
export const attachGeometry = (fields, layoutFields) => {
  const byShort = new Map();
  const byExact = new Map();
  const byNorm = new Map();
  (layoutFields || []).forEach((widget) => {
    const push = (map, key) => {
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(widget);
    };
    push(byShort, widget.formstack_name);
    push(byExact, widget.field_name);
    push(byNorm, normalizeName(widget.formstack_name || widget.field_name));
  });

  const claimed = new Set();
  const placed = (fields || []).map((field) => {
    const name = field.field_name;
    const rects =
      byShort.get(name) || byExact.get(name) || byNorm.get(normalizeName(name)) || [];
    rects.forEach((r) => claimed.add(r));
    return { ...field, rects };
  });

  const orphans = (layoutFields || []).filter((widget) => !claimed.has(widget));
  return { fields: placed, orphans };
};

export const fieldState = (field, mappings) => {
  if (mappings?.[field.field_name]?.sf_object) return "mapped";
  return "unmapped";
};

export const widgetState = (widget, field, mappings) => {
  if (widget.widget_type === "signature") return "signature";
  if (field && mappings?.[field.field_name]?.sf_object) return "mapped";
  if (widget.widget_type === "checkbox" || widget.widget_type === "radio") return "checkbox";
  return "unmapped";
};

/** Screen pixels for a PDF-point rectangle on a page rendered at `scale`. */
export const toPixels = (rect, scale) => ({
  left: rect.x * scale,
  top: rect.y * scale,
  width: rect.width * scale,
  height: rect.height * scale,
});

/** PDF points for a pixel rectangle, clamped inside the page. */
export const toPoints = (pixels, page, scale) => {
  const width = Math.max(8, pixels.width / scale);
  const height = Math.max(8, pixels.height / scale);
  const x = Math.min(Math.max(0, pixels.left / scale), Math.max(0, page.width - width));
  const y = Math.min(Math.max(0, pixels.top / scale), Math.max(0, page.height - height));
  return {
    x: Math.round(x * 100) / 100,
    y: Math.round(y * 100) / 100,
    width: Math.round(width * 100) / 100,
    height: Math.round(height * 100) / 100,
  };
};

let localBoxId = 0;
export const newBoxId = () => {
  localBoxId += 1;
  return `new-${localBoxId}`;
};

/** A box centred on a click, in PDF points, for the chosen type. */
export const boxAtPoint = ({ x, y }, page, { role, fieldType }) => {
  const type = BOX_TYPES.find((t) => t.value === fieldType) || BOX_TYPES[0];
  const width = Math.min(type.width, page.width);
  const height = Math.min(type.height, page.height);
  return {
    id: newBoxId(),
    role,
    field_type: type.value,
    label: "",
    page_number: page.page_number,
    ...toPoints(
      { left: x - width / 2, top: y - height / 2, width, height },
      page,
      1
    ),
  };
};

// Two y axes meet here. The layout (and everything drawn on screen) measures
// y from the page's TOP edge. Saved signature boxes use standard PDF space:
// y is the box's top edge measured from the page's BOTTOM (what Apryse's
// getPDFCoordinates returns and what the e-sign stamping expects). Both use
// the same x, width and height; only y flips: saved_y = page_height - top_y.
const pageHeightFor = (pages, pageNumber) =>
  Number((pages || []).find((p) => p.page_number === Number(pageNumber))?.height) || 792;

export const savedFieldsToStudioBoxes = (fields, pages) =>
  (fields || [])
    .filter((f) => ESIGN_BOX_TYPES.has(f.field_type) && f.is_active !== false)
    .map((f) => {
      const pageNumber = Number(f.page_number) || 1;
      const round = (v) => Math.round(v * 100) / 100;
      return {
        id: f.id ? `saved-${f.id}` : newBoxId(),
        role: f.role || "client",
        field_type: f.field_type,
        label: f.label || "",
        page_number: pageNumber,
        x: Number(f.x),
        y: round(pageHeightFor(pages, pageNumber) - Number(f.y)),
        width: Number(f.width),
        height: Number(f.height),
      };
    });

export const studioBoxesToSavedFields = (boxes, pages) =>
  (boxes || []).map((box, index) => ({
    role: box.role,
    field_type: box.field_type,
    label: box.label || "",
    page_number: box.page_number,
    x: box.x,
    y: Math.round((pageHeightFor(pages, box.page_number) - box.y) * 100) / 100,
    width: box.width,
    height: box.height,
    font_size: 1,
    required: true,
    hidden: true,
    order: index,
    is_active: true,
  }));

/** "Client + Attorney" — who signs, in the order roles are offered. */
export const signersSummary = (boxes) => {
  const roles = new Set((boxes || []).map((b) => b.role));
  return SIGNER_ROLES.filter((r) => roles.has(r.value))
    .map((r) => r.label)
    .join(" + ");
};

/**
 * The full save payload for the mappings endpoint. It replaces everything,
 * so the entries the studio does not edit (conditional sections, merge
 * fields that are not on the page) must ride along unchanged.
 */
export const buildMappingPayload = (mappings, { conditionalMappings = [], mergeFieldMappings = [] } = {}) => {
  const direct = Object.entries(mappings || {})
    .filter(([, m]) => m && m.sf_object && m.sf_field_name)
    .map(([fieldName, m]) => ({
      template_field_name: fieldName,
      sf_object: m.sf_object,
      sf_field_name: m.sf_field_name,
      transformation: m.transformation || "",
      logic_rules: m.logic_rules && Object.keys(m.logic_rules).length ? m.logic_rules : {},
    }));
  const conditional = conditionalMappings
    .filter((c) => c.template_field_name && c.sf_object && c.sf_field_name)
    .map((c) => ({
      template_field_name: c.template_field_name,
      sf_object: c.sf_object,
      sf_field_name: c.sf_field_name,
      transformation: "",
      is_conditional: true,
      conditional_notes: c.conditional_notes || "",
    }));
  const merge = mergeFieldMappings
    .filter((m) => m.template_field_name && m.sf_object && m.sf_field_name)
    .map((m) => ({
      template_field_name: m.template_field_name,
      sf_object: m.sf_object,
      sf_field_name: m.sf_field_name,
      transformation: m.transformation || "",
    }));
  return [...direct, ...conditional, ...merge];
};

export const mappingsFromFields = (fields) => {
  const initial = {};
  (fields || []).forEach((f) => {
    if (f.mapping && f.mapping.sf_object) {
      initial[f.field_name] = {
        sf_object: f.mapping.sf_object,
        sf_field_name: f.mapping.sf_field_name,
        transformation: f.mapping.transformation || "",
        logic_rules: f.mapping.logic_rules || {},
      };
    }
  });
  return initial;
};

export const filterFields = (fields, { query, mode, mappings }) => {
  const q = String(query || "").trim().toLowerCase();
  return (fields || []).filter((f) => {
    if (q && !`${f.field_name} ${f.field_label || ""}`.toLowerCase().includes(q)) return false;
    const state = fieldState(f, mappings);
    if (mode === "mapped") return state === "mapped";
    if (mode === "unmapped") return state === "unmapped";
    if (mode === "offpage") return !f.rects || f.rects.length === 0;
    return true;
  });
};
